/**
 * packages/ui/courtRuleCite.mjs — RUN RQ · §2. **IS THIS CITATION A RULE OF COURT?** ONE COPY.
 *
 * 🔴 EVERY PATTERN AND EVERY WORD BELOW IS RUN VF's, MOVED, NOT REWRITTEN. It lived in
 * `apps/steel/server/lib/courtRules.js`, which is a SERVER file — and RUN RQ needs the same
 * question answered on the CLIENT, because a frozen or stored envelope carries the `kind` the
 * server wrote when it was captured, and correcting it only on the write path leaves every
 * already-captured row wearing the wrong pill forever. MEASURED: the homepage chip's frozen
 * `Fed. R. Civ. P. 56` row still rendered a **Case** pill after the server-side fix, because that
 * envelope was frozen before it.
 *
 * ⭐ SO THE RULE MOVES TO THE ONE PLACE BOTH SIDES CAN READ IT, and `courtRules.js` now imports it
 * rather than holding a second copy. Its own job — DROPPING rule mentions before resolution — and
 * its tests are untouched; only the recognizer's address changed.
 *
 * ⚠️ VF's fences are load-bearing and are kept verbatim: the U.S.C./C.F.R. check runs FIRST so a
 * hybrid string resolves on its enacted-law half; the local-rule lookbehind keeps `N.Y. C.P.L.R.
 * 3211` (enacted law this corpus HOLDS) out; and every pattern requires the abbreviated `R.`/`L.R.`
 * shape or an explicit "Rule(s) of ..." phrase, never the bare word "procedure", so New York's
 * C.P.L.R. and California's Code of Civil Procedure are not swallowed. Probed by VF against all 207
 * distinct statute-kind cites this repo has recorded: 39 flagged, 0 false positives on the 83 that
 * have actually resolved.
 */
/**
 * Federal rule sets, abbreviated: `Fed. R. Civ. P. 12(b)(6)`, `Fed R Crim P 52(b)`,
 * `Fed. R. Evid. 702`, `Fed. R. App. P. 4(a)(1)`, `Fed. R. Bankr. P. 3002`.
 */
const FED_ABBREV = /\bFed\.?\s*R\.?\s*(?:Civ|Crim|App|Bankr|Evid)\b/i;

/**
 * The same sets spelled out: `Federal Rule of Civil Procedure 12(b)(6)`,
 * `Federal Rules of Evidence 702`.
 */
const FED_SPELLED = /\bFederal\s+Rules?\s+of\s+(?:Civil|Criminal|Appellate|Bankruptcy)\s+Procedure\b|\bFederal\s+Rules?\s+of\s+Evidence\b/i;

/**
 * State and territorial rules of court, abbreviated. The jurisdiction prefix is deliberately NOT
 * enumerated — `Me. R. Civ. P. 60(b)`, `Tex. R. Civ. P. 91a`, `Ariz. R. Fam. L.P. 47` — because the
 * load-bearing signal is the ` R. <set> ` shape, not the state. The trailing `\s*\d` is what keeps
 * this off prose: a rule citation names a rule number.
 */
const STATE_ABBREV = /\bR\.?\s*(?:Civ|Crim|App|Evid|Bankr|Juv|Fam|Prob|Super|Sup|Dist|Mun|Prof|Jud)\.?\s*(?:P|R|L\.?\s*P|Conduct)?\.?\s*\d/i;

/** Rules of court spelled out, any jurisdiction: `Maine Rules of Civil Procedure 60(b)`. */
const RULES_OF = /\bRules?\s+of\s+(?:Civil|Criminal|Appellate|Juvenile|Family|Probate|Bankruptcy)\s+Procedure\b|\bRules?\s+of\s+Evidence\b|\bRules?\s+of\s+(?:Professional|Judicial)\s+Conduct\b/i;

/**
 * Local rules of a district: `N.D. Cal. Civ. L.R. 7-2`, `L. Civ. R. 7.1`, `Local Rule 56.1`,
 * `Local Civil Rule 7`. `L.R.`/`L. R.` is required to be followed by a number for the same reason.
 *
 * 🔴 THE LOOKBEHIND IS LOAD-BEARING AND IT COST A LIVE AUTHORITY TO FIND. Without `(?<![A-Za-z.])`
 * this pattern reads the `L.R. 3211` sitting inside **`N.Y. C.P.L.R. 3211`** and drops it — and
 * CPLR 3211 is New York's *Motion to dismiss* section, ENACTED law, which this corpus HOLDS and the
 * resolver returns by name today (`ops/run-vf/out/rule-guard-live.json`). New York calls its code
 * the Civil Practice Law and **Rules**; the abbreviation collides with a local-rule cite and the
 * law behind it is a statute. A local rule's `L` always begins a token — `Civ. L.R.`, `L. Civ. R.`
 * — so requiring that is exactly the distinction and costs nothing else.
 */
const LOCAL = /(?<![A-Za-z.])(?:Civ\.?\s*)?L\.?\s*(?:Civ\.?\s*)?R\.?\s*\d|\bLocal\s+(?:Civil\s+|Criminal\s+|Patent\s+|Admiralty\s+)?Rules?\s+\d/i;

/** Supreme Court rules: `Sup. Ct. R. 10`, `S. Ct. R. 33`. */
const SCOTUS_RULE = /\bS(?:up)?\.?\s*Ct\.?\s*R\.?\s*\d/i;

/**
 * 🔴 RUN SC — THE `<Jur>. Ct. R. <n>` FORM, WHICH IS HOW HALF THE STATES CITE THEIR RULES.
 *
 * `STATE_ABBREV` requires a PRACTICE-AREA token between the `R.` and the number — `R. Civ. P. 33`.
 * A large family of state rules is cited with no such token: the court is named instead of the
 * rule set. Measured over 20 real state and federal rule citation forms, 9 were not recognised,
 * and every one of the nine reached `seeAlsoKind` as **`case`** — the exact defect RUN RQ fixed
 * for `Fed. R. Civ. P. 56`, still live in the state form:
 *
 *     N.J. Ct. R. 4:17-1(b)(ii)   ← ADAM'S OWN PROBE. A New Jersey court rule, wearing a Case pill.
 *     N.Y. Ct. R. 202.5 · Cal. Ct. R. 8.204 · Ga. Ct. R. 5.2 · Mich. Ct. R. 2.309 · Wash. Ct. R. 33
 *
 * ⚠️ `Ct.` ALONE IS NOT ENOUGH AND THE `R.` IS WHAT MAKES THIS SAFE. `S. Ct.` and `Ct. Cl.` open
 * reporter citations (`86 S. Ct. 1602`), and neither puts an `R.` and a number after the `Ct.`
 * `SCOTUS_RULE` already relies on exactly this shape; this is the same test without the `S`.
 */
const CT_RULE = /\bCt\.?\s*R\.?\s*\d/i;

/**
 * The set token BEFORE the `R.` rather than after it: `Ohio Civ. R. 33`, `Tenn. Crim. App. R. 8`.
 * `STATE_ABBREV` reads `R.` then set; this reads set then `R.`, and both end at a rule number.
 */
const SET_THEN_R = /\b(?:Civ|Crim|App|Evid|Bankr|Juv|Fam|Prob)\.?\s*R\.?\s*\d/i;

/** Connecticut names its rules of court the Practice Book: `Conn. Practice Book § 13-6`. */
const PRACTICE_BOOK = /\bPractice\s+Book\b/i;

/**
 * `Rule` spelled out behind a jurisdiction abbreviation: `Md. Rule 2-421`, `Del. Rule 26`.
 * 🔴 THE ABBREVIATION AND ITS PERIOD ARE REQUIRED. A bare `\bRules?\s+\d` reads **`Rule 10b-5`**,
 * which `source-types.js` lists as a `resolve_cfr` REGULATION example, and reads ordinary prose.
 * Demanding a `Xx.` token in front is what separates a citation from a sentence.
 */
const JUR_RULE = /\b[A-Z][A-Za-z]{0,4}\.\s*Rules?\s+\d/;

const PATTERNS = [FED_ABBREV, FED_SPELLED, STATE_ABBREV, RULES_OF, LOCAL, SCOTUS_RULE,
                  CT_RULE, SET_THEN_R, PRACTICE_BOOK, JUR_RULE];


/**
 * Is this citation string a rule of court rather than enacted law?
 *
 * 🔴 A U.S.C. OR C.F.R. CITATION IS NEVER A RULE, WHATEVER ELSE IT CONTAINS. `28 U.S.C. app. Fed.
 * R. Civ. P.` and similar hybrid strings do occur, and the enacted-law half is the half that
 * resolves. Checked FIRST so no rule pattern can reach a citation that names a code.
 *
 * @param {string|null|undefined} cite
 * @returns {boolean}
 */
export function isCourtRuleCite(cite) {
  const s = String(cite || '').trim();
  if (!s) return false;
  if (/\bU\.?\s*S\.?\s*C\.?\b|\bC\.?\s*F\.?\s*R\.?\b/i.test(s) && /\d+\s*(?:U\.?\s*S\.?\s*C|C\.?\s*F\.?\s*R)/i.test(s)) return false;
  return PATTERNS.some((re) => re.test(s));
}

/**
 * A C.F.R. section or a Treasury Regulation — the registry's `resolve_cfr` / **Regulation**.
 *
 * 🔴 A SEPARATE RECOGNIZER, NOT A WIDENING OF `isCourtRuleCite`. RUN VF: *"`isCourtRuleCite` must
 * stay a rules recognizer: widening it later is a decision someone makes on purpose, not a regex
 * that drifted"* — and it explicitly returns FALSE for C.F.R. so hybrids resolve on their
 * enacted-law half. Two questions, two functions.
 *
 * ⚠️ The numeric form REQUIRES a leading title number, so prose mentioning "the CFR" cannot match.
 */
const CFR_NUMBERED = /\b\d+\s*C\.?\s*F\.?\s*R\.?(?![A-Za-z])/i;
const CFR_SHAPED = /\bC\.?\s*F\.?\s*R\.?\s*(?:§|Part\b|pt\.)/i;
const TREAS_REG = /\bTreas\.?\s*Reg\b/i;

export function isRegulationCite(cite) {
  const s = String(cite || '').trim();
  if (!s) return false;
  return CFR_NUMBERED.test(s) || CFR_SHAPED.test(s) || TREAS_REG.test(s);
}

/**
 * The `kind` a SEE-ALSO row should be rendered as — asked of the CITE, never of the model's own
 * `kind` field, which is the field the whole defect hung on.
 *
 * 🔴 IT RUNS ON BOTH THE WRITE PATH AND THE READ PATH. Server-side it stamps the envelope
 * (clarkAnswerV2.js); client-side both rails call it again, because a FROZEN or STORED envelope
 * carries whatever `kind` was written when it was captured. MEASURED: the homepage chip's frozen
 * `Fed. R. Civ. P. 56` row still drew a **Case** pill after the server fix alone.
 *
 * @param {{kind?: string|null, cite?: string|null}} row
 * @returns {'statute'|'regulation'|'case'}
 */
export function seeAlsoKind(row) {
  const cite = row?.cite;
  if (isCourtRuleCite(cite)) return 'statute';       // registry: resolve_statute covers court rules
  if (isRegulationCite(cite)) return 'regulation';   // registry: resolve_cfr — "Regulation"
  return row?.kind === 'statute' ? 'statute' : 'case';
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// RUN FR — THE LOOKUP. THE SHAPE CHECK IS THE FAST PATH; THE CORPUS IS THE VERDICT.
//
// 🔴 THE DEFECT THIS CLOSES, IN ONE LINE: `isCourtRuleCite("Fed. R. Civ. P. 999")` returns **true**.
// It always did, correctly — it is a SHAPE recognizer and 999 is rule-shaped. But with the federal
// rules now in the corpus there is a fact to consult, and a function that can check and does not is
// guessing. RUN CX needs rules admissible to SEE ALSO, and admitting a rule that does not exist is
// worse than admitting nothing.
//
// 🔴 `isCourtRuleCite` IS NOT TOUCHED, AND THAT IS DELIBERATE. It is the fast path, it is what
// `seeAlsoKind` and `courtRules.js` call, and RUN SC's 140,000-string regression is calibrated on
// its exact behaviour. Widening or narrowing it to carry a lookup would put every one of those
// strings back in play to buy a verdict that belongs in a separate function. Two questions:
//   "is this rule-SHAPED?"  -> isCourtRuleCite   (sync, no data, unchanged since RUN SC)
//   "does this rule EXIST?" -> knownCourtRule    (sync, reads the shipped registry)
//
// 🔴 WHY THE REGISTRY IS SHIPPED AND NOT QUERIED. `seeAlsoKind` runs in the BROWSER
// (SeeAlsoCards.jsx:71) and is SYNCHRONOUS — it decides a pill during render. RUN RQ moved this
// module to packages/ui precisely so ONE copy answers on both sides; a server-only verdict would
// re-open that split and send every frozen envelope back to the wrong pill. So the corpus is
// compiled to an 11 kB manifest of what exists.
//
// ⚠️ `null` IS A THIRD ANSWER AND IT IS THE HONEST ONE. A state rule — `N.J. Ct. R. 4:17-1` — is
// rule-shaped, real, and NOT in this federal corpus. Returning `false` for it would call a real rule
// fake. `knownCourtRule` returns:
//     true   — this corpus holds it
//     false  — it is a FEDERAL set this corpus holds IN FULL, and this rule is not in it
//     null   — out of corpus scope (a state or local rule); nothing is claimed either way
// Only `false` is a finding. That distinction is what keeps this from becoming the confident-wrong
// answer it exists to prevent.
import REGISTRY from './courtRuleRegistry.mjs';

/** The five federal sets, in the orthographies this repo has recorded. Longest alternative first. */
const FED_SET_PATTERNS = [
  ['frcp',  /\b(?:Fed(?:eral)?\.?\s*R(?:ules?)?\.?\s*Civ(?:il)?\.?\s*P(?:roc(?:edure)?)?\.?|FRCP)\b/i],
  ['frcrp', /\b(?:Fed(?:eral)?\.?\s*R(?:ules?)?\.?\s*Crim(?:inal)?\.?\s*P(?:roc(?:edure)?)?\.?|FRCrP)\b/i],
  ['frap',  /\b(?:Fed(?:eral)?\.?\s*R(?:ules?)?\.?\s*App(?:ellate)?\.?\s*P(?:roc(?:edure)?)?\.?|FRAP)\b/i],
  ['frbp',  /\b(?:Fed(?:eral)?\.?\s*R(?:ules?)?\.?\s*Bankr(?:uptcy)?\.?\s*P(?:roc(?:edure)?)?\.?|FRBP)\b/i],
  ['fre',   /\b(?:Fed(?:eral)?\.?\s*R(?:ules?)?\.?\s*Evid(?:ence)?\.?|FRE)\b/i],
];
const RULE_NUMBER = /\b(\d+(?:\.\d+)?|[A-G])\s*((?:\([A-Za-z0-9]{1,4}\))*)/;

/**
 * Parse a federal rule citation into the corpus's own coordinates.
 * @returns {{set:string, number:string, subs:string[], identifier:string}|null}
 *   null when the string names no federal set this corpus holds — a state rule, a local rule, prose.
 */
export function parseFederalRuleCite(cite) {
  const s = String(cite || '').trim();
  if (!s) return null;
  for (const [set, re] of FED_SET_PATTERNS) {
    const m = re.exec(s);
    if (!m) continue;
    const rest = s.slice(m.index + m[0].length);
    const n = RULE_NUMBER.exec(rest);
    if (!n) return null;
    const subs = (n[2] || '').match(/\([A-Za-z0-9]{1,4}\)/g)?.map((x) => x.slice(1, -1)) || [];
    return { set, number: n[1], subs,
             identifier: `/us/${set}/r${n[1]}` + (subs.length ? '/' + subs.join('/') : '') };
  }
  return null;
}

/**
 * Does this rule EXIST? true | false | null — see the header for why `null` is not `false`.
 * @param {string|null|undefined} cite
 */
export function knownCourtRule(cite) {
  const p = parseFederalRuleCite(cite);
  if (!p) return null;                                   // out of corpus scope; claim nothing
  const subs = REGISTRY.rules?.[p.set]?.[p.number];
  if (subs === undefined) return false;                  // the set is held IN FULL; this rule is not in it
  if (!p.subs.length) return true;
  // ⚠️ ONLY THE FIRST SUBDIVISION IS VERIFIED. The manifest encodes one level (see gen_registry).
  // A deeper pincite falls back to the rule's existence rather than claiming a check it did not do.
  const first = String(p.subs[0]).toLowerCase();
  if (!/^[a-z]$/.test(first)) return true;               // numeric first level (FRBP style) — not indexed
  return subs.includes(first);
}

/** The status of a rule this corpus holds: null when live or unknown, else 'abrogated' etc. */
export function courtRuleStatus(cite) {
  const p = parseFederalRuleCite(cite);
  if (!p) return null;
  const set = REGISTRY.sets?.[p.set];
  return REGISTRY.notCurrent?.[`${set?.prefix} ${p.number}`] || null;
}

/**
 * The full verdict, for callers that must show their work (Manual Cite, Magic Cite, SEE ALSO).
 * `shape` is the fast path's answer; `known` is the corpus's.
 */
export function courtRuleVerdict(cite) {
  const shape = isCourtRuleCite(cite);
  const parsed = parseFederalRuleCite(cite);
  const known = knownCourtRule(cite);
  const set = parsed ? REGISTRY.sets?.[parsed.set] : null;
  return {
    shape,
    known,
    identifier: known === true && parsed ? parsed.identifier : null,
    ruleSet: parsed?.set ?? null,
    setName: set?.name ?? null,
    status: courtRuleStatus(cite),
    // 🔴 CURRENCY TRAVELS WITH THE VERDICT. §3: the date is surfaced in the ANSWER, not just on the
    // row. A caller that can render "this rule exists" and cannot render "as of when" is the defect
    // RUN SC found in the federal statutes, arriving in a new corpus.
    asOf: set?.asOf ?? null,
    sourceRelease: set?.release ?? null,
    reason: !shape ? 'not rule-shaped'
          : known === null ? 'rule-shaped, but not a federal set this corpus holds'
          : known === false ? `no such rule in ${set?.name || parsed?.set}`
          : 'in corpus',
  };
}

export default isCourtRuleCite;
